import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  Close,
  ExpandMore,
  Settings,
  Save,
  Restore,
  Calculate,
  TrendingUp,
  Warning,
  Info
} from '@mui/icons-material';

// Default Configuration Constants
const DEFAULT_CALCULATION_CONFIG = {
  // Impact Weights
  impactWeights: {
    VERY_HIGH: 10,
    HIGH: 8,
    MEDIUM: 6,
    LOW: 4
  },
  
  // Recoverability Weights
  recoverabilityWeights: {
    HIGH: 10,
    MEDIUM: 7,
    LOW: 4
  },
  
  // Priority Weights
  priorityWeights: {
    IMMEDIATE: 10,
    HIGH: 8,
    MEDIUM: 6,
    LOW: 4,
    LONG_TERM: 3
  },
  
  // Category Weights
  categoryWeights: {
    financial: 25,
    strategic: 20,
    digital: 18,
    operational: 16,
    humanCapital: 15,
    customer: 14,
    innovation: 13,
    compliance: 12,
    ecosystem: 11,
    technology: 10,
    process: 9,
    data: 8,
    supplyChain: 7,
    quality: 6,
    sustainability: 5,
    governance: 4,
    cultural: 3,
    quantum: 2
  },
  
  // Pain Point Base Weights
  painPointBaseWeights: {
    duplicateSystems: 9,
    underutilizedLicenses: 8,
    inefficientProcurement: 8,
    pricingInefficiencies: 9,
    competitiveBlindspots: 9,
    innovationPipelineFriction: 8,
    marketShiftDelays: 8,
    unmonetizedData: 9,
    legacySystemDrag: 8,
    aiCapabilityGaps: 9,
    manualProcesses: 8,
    supplyChainInefficiencies: 7,
    qualityRework: 7,
    talentTurnover: 9,
    skillGaps: 8,
    leadershipGaps: 8,
    customerChurn: 9,
    onboardingFriction: 7,
    upsellMissed: 8,
    slowTimeToMarket: 8,
    regulatoryPenalties: 9,
    partnershipInefficiencies: 7,
    integrationGaps: 7,
    collaborationInefficiencies: 6,
    dataQualityIssues: 7,
    logisticsInefficiencies: 6,
    customerComplaints: 6,
    carbonFootprint: 5,
    decisionDelays: 7,
    innovationResistance: 6,
    quantumAwarenessGap: 8
  },
  
  // Business Case Multipliers
  businessCaseMultipliers: {
    immediateValueHighPriority: 10,
    immediateValueMediumPriority: 5,
    strategicValuePerPoint: 25,
    totalValueHighPriority: 15,
    totalValueMediumPriority: 8,
    totalValueBase: 3,
    efficiencyImprovementBase: 10,
    efficiencyImprovementPerPoint: 3,
    efficiencyImprovementMax: 40,
    gccImpactBase: 15,
    gccImpactPerPoint: 2,
    gccImpactMax: 50,
    totalAddressableMultiplier: 50
  },
  
  // Timeline Constants
  timelineConstants: {
    roiMinMonths: 6,
    roiMaxMonths: 36,
    roiReductionPerPoint: 1,
    timelineReductionFactor: 2
  },
  
  // Recovery Percentage Ranges
  recoveryPercentages: {
    duplicateSystems: { min: 15, max: 25 },
    underutilizedLicenses: { min: 20, max: 35 },
    inefficientProcurement: { min: 15, max: 30 },
    pricingInefficiencies: { min: 5, max: 20 },
    competitiveBlindspots: { min: 30, max: 50 },
    innovationPipelineFriction: { min: 40, max: 60 },
    marketShiftDelays: { min: 25, max: 45 },
    unmonetizedData: { min: 15, max: 30 },
    legacySystemDrag: { min: 30, max: 50 },
    aiCapabilityGaps: { min: 40, max: 60 },
    manualProcesses: { min: 20, max: 40 },
    supplyChainInefficiencies: { min: 20, max: 35 },
    qualityRework: { min: 15, max: 30 },
    talentTurnover: { min: 30, max: 50 },
    skillGaps: { min: 25, max: 45 },
    leadershipGaps: { min: 30, max: 60 },
    customerChurn: { min: 15, max: 25 },
    onboardingFriction: { min: 25, max: 45 },
    upsellMissed: { min: 20, max: 35 },
    slowTimeToMarket: { min: 40, max: 60 },
    regulatoryPenalties: { min: 0, max: 0 }, // Avoidance based
    partnershipInefficiencies: { min: 200, max: 300 }, // ROI multiplier
    integrationGaps: { min: 30, max: 50 },
    collaborationInefficiencies: { min: 20, max: 35 },
    dataQualityIssues: { min: 25, max: 40 },
    logisticsInefficiencies: { min: 15, max: 30 },
    customerComplaints: { min: 20, max: 35 },
    carbonFootprint: { min: 10, max: 20 },
    decisionDelays: { min: 40, max: 60 },
    innovationResistance: { min: 40, max: 70 },
    quantumAwarenessGap: { min: 50, max: 100 } // Computation advantage multiplier
  },
  
  // Impact Ranges (in millions)
  impactRanges: {
    duplicateSystems: { min: 0.5, max: 5 },
    underutilizedLicenses: { min: 0.25, max: 2 },
    inefficientProcurement: { min: 0.75, max: 4 },
    pricingInefficiencies: { min: 3, max: 15 }, // Percentage of revenue
    competitiveBlindspots: { min: 50, max: 200 },
    innovationPipelineFriction: { min: 100, max: 500 },
    marketShiftDelays: { min: 100, max: 500 },
    unmonetizedData: { min: 10, max: 25 }, // Percentage of revenue
    legacySystemDrag: { min: 25, max: 40 }, // Percentage of IT budget
    aiCapabilityGaps: { min: 40, max: 60 }, // Percentage efficiency
    manualProcesses: { min: 1, max: 8 },
    supplyChainInefficiencies: { min: 15, max: 25 }, // Percentage carrying costs
    qualityRework: { min: 5, max: 15 }, // Percentage of total cost
    talentTurnover: { min: 50, max: 200 }, // Percentage of annual salary
    skillGaps: { min: 20, max: 40 }, // Percentage productivity loss
    leadershipGaps: { min: 25, max: 50 }, // Percentage team performance
    customerChurn: { min: 10, max: 30 }, // Percentage revenue at risk
    onboardingFriction: { min: 30, max: 60 }, // Percentage early churn risk
    upsellMissed: { min: 15, max: 40 }, // Percentage revenue opportunity
    slowTimeToMarket: { min: 20, max: 60 }, // Percentage market share loss
    regulatoryPenalties: { min: 1, max: 100 },
    partnershipInefficiencies: { min: 40, max: 70 }, // Percentage value unrealized
    integrationGaps: { min: 20, max: 40 }, // Percentage process time
    collaborationInefficiencies: { min: 15, max: 30 }, // Percentage productive time
    dataQualityIssues: { min: 15, max: 30 }, // Percentage decision quality loss
    logisticsInefficiencies: { min: 10, max: 25 }, // Percentage cost premium
    customerComplaints: { min: 5, max: 15 }, // Percentage satisfaction impact
    carbonFootprint: { min: 2, max: 8 }, // Percentage operational costs
    decisionDelays: { min: 25, max: 50 }, // Percentage opportunity loss
    innovationResistance: { min: 30, max: 60 }, // Percentage adoption failure
    quantumAwarenessGap: { min: 2, max: 3 } // Years to irrelevance
  },
  
  // GCC Specific Constants
  gccConstants: {
    establishmentPhase: {
      wrongLocation: { min: 20, max: 40 },
      incorrectCapability: { min: 30, max: 60 },
      knowledgeTransfer: { min: 25, max: 50 }
    },
    operationalPhase: {
      serviceQuality: { min: 15, max: 35 },
      talentAttrition: { min: 20, max: 40 },
      innovationCeiling: { min: 50, max: 80 }
    },
    valueAmplification: {
      centerOfExcellence: { min: 60, max: 80 },
      globalNetwork: { value: 0 }, // No advantages
      innovationHub: { value: 0 } // Follower position
    }
  },
  
  // Pain Score Thresholds
  painScoreThresholds: {
    critical: 8,
    high: 6,
    medium: 4,
    low: 0
  },
  
  // Default Fallback Values
  fallbackValues: {
    impactScore: 6,
    recoverabilityScore: 6,
    priorityScore: 6,
    painWeight: 5,
    customPainPointScore: 6.5
  }
};

const CalculationConfigurationPanel = ({ 
  open = false, 
  onClose, 
  onSave,
  currentConfig = DEFAULT_CALCULATION_CONFIG 
}) => {
  const [config, setConfig] = useState(currentConfig);
  const [activeSection, setActiveSection] = useState('weights');
  const [hasChanges, setHasChanges] = useState(false);

// Add this useEffect after the state declarations
useEffect(() => {
  if (currentConfig && JSON.stringify(currentConfig) !== JSON.stringify(config)) {
    setConfig(JSON.parse(JSON.stringify(currentConfig)));
    setHasChanges(false);
  }
}, [currentConfig, open]); // Reset when dialog opens or currentConfig changes


  // Handle configuration changes
  const handleConfigChange = (section, key, value, subKey = null) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      if (subKey) {
        newConfig[section][key][subKey] = value;
      } else {
        newConfig[section][key] = value;
      }
      return newConfig;
    });
    setHasChanges(true);
  };


  // Reset to defaults
// In CalculationConfigurationPanel.js - Update the handleReset function
const handleReset = () => {
  setConfig(JSON.parse(JSON.stringify(DEFAULT_CALCULATION_CONFIG))); // Deep clone
  setHasChanges(true);
  
  // Force immediate UI update by resetting active section
  setActiveSection('weights');
};

// Also update the onSave in the parent call to close dialog after save
const handleSave = () => {
  if (onSave) {
    onSave(config);
  }
  setHasChanges(false);
  // Optionally close the dialog after save
  // onClose(); // Uncomment if you want to auto-close after save
};



  // Section components for better organization
  const WeightsSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Scoring Weights Configuration
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        These weights determine how different factors contribute to the overall pain score calculation.
      </Alert>

      <Grid container spacing={3}>
        {/* Impact Weights */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Impact Weights
              </Typography>
              {Object.entries(config.impactWeights).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">
                      {key.replace('_', ' ')}
                    </Typography>
                    <Chip label={value} size="small" />
                  </Box>
                  <Slider
                    value={value}
                    onChange={(e, newValue) => handleConfigChange('impactWeights', key, newValue)}
                    min={1}
                    max={10}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Recoverability Weights */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Recoverability Weights
              </Typography>
              {Object.entries(config.recoverabilityWeights).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">
                      {key}
                    </Typography>
                    <Chip label={value} size="small" />
                  </Box>
                  <Slider
                    value={value}
                    onChange={(e, newValue) => handleConfigChange('recoverabilityWeights', key, newValue)}
                    min={1}
                    max={10}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Priority Weights */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Priority Weights
              </Typography>
              {Object.entries(config.priorityWeights).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">
                      {key.replace('_', ' ')}
                    </Typography>
                    <Chip label={value} size="small" />
                  </Box>
                  <Slider
                    value={value}
                    onChange={(e, newValue) => handleConfigChange('priorityWeights', key, newValue)}
                    min={1}
                    max={10}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Category Weights */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Category Weights (%)
              </Typography>
              {Object.entries(config.categoryWeights).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                    <Chip label={`${value}%`} size="small" />
                  </Box>
                  <Slider
                    value={value}
                    onChange={(e, newValue) => handleConfigChange('categoryWeights', key, newValue)}
                    min={1}
                    max={30}
                    step={1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v}%`}
                    size="small"
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const BusinessCaseSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Business Case Calculation Parameters
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        These multipliers determine how selected pain points translate into financial estimates and ROI calculations.
      </Alert>

      <Grid container spacing={3}>
        {/* Business Case Multipliers */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Value Calculation Multipliers ($M)
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(config.businessCaseMultipliers).map(([key, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom sx={{ textTransform: 'capitalize' }}>
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </Typography>
                      <TextField
                        fullWidth
                        type="number"
                        value={value}
                        onChange={(e) => handleConfigChange('businessCaseMultipliers', key, Number(e.target.value))}
                        size="small"
                        inputProps={{ min: 1, max: 1000 }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Timeline Constants */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Timeline Constants (Months)
              </Typography>
              {Object.entries(config.timelineConstants).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom sx={{ textTransform: 'capitalize' }}>
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(e) => handleConfigChange('timelineConstants', key, Number(e.target.value))}
                    size="small"
                    inputProps={{ min: 1, max: 60 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Pain Score Thresholds */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Pain Score Thresholds
              </Typography>
              {Object.entries(config.painScoreThresholds).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom sx={{ textTransform: 'capitalize' }}>
                    {key} Threshold
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={value}
                    onChange={(e) => handleConfigChange('painScoreThresholds', key, Number(e.target.value))}
                    size="small"
                    inputProps={{ min: 0, max: 10, step: 0.1 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const ImpactRangesSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Impact Range Configuration
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Configure the minimum and maximum impact ranges for each value leakage type (in $M unless specified as percentage).
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Leakage Type</TableCell>
              <TableCell align="center">Min Impact</TableCell>
              <TableCell align="center">Max Impact</TableCell>
              <TableCell>Unit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(config.impactRanges).map(([key, range]) => {
              const isPercentage = key.includes('Percentage') || 
                                 ['pricingInefficiencies', 'legacySystemDrag', 'aiCapabilityGaps', 
                                  'supplyChainInefficiencies', 'qualityRework', 'talentTurnover',
                                  'skillGaps', 'leadershipGaps', 'customerChurn', 'onboardingFriction',
                                  'upsellMissed', 'slowTimeToMarket', 'partnershipInefficiencies',
                                  'integrationGaps', 'collaborationInefficiencies', 'dataQualityIssues',
                                  'logisticsInefficiencies', 'customerComplaints', 'carbonFootprint',
                                  'decisionDelays', 'innovationResistance'].includes(key);
              
              return (
                <TableRow key={key}>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={range.min}
                      onChange={(e) => handleConfigChange('impactRanges', key, { ...range, min: Number(e.target.value) })}
                      size="small"
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={range.max}
                      onChange={(e) => handleConfigChange('impactRanges', key, { ...range, max: Number(e.target.value) })}
                      size="small"
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={isPercentage ? '%' : '$M'} 
                      size="small" 
                      color={isPercentage ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const RecoverySection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Recovery Potential Configuration
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Set the minimum and maximum recovery percentages for each value leakage type.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Leakage Type</TableCell>
              <TableCell align="center">Min Recovery %</TableCell>
              <TableCell align="center">Max Recovery %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(config.recoveryPercentages).map(([key, range]) => (
              <TableRow key={key}>
                <TableCell>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    value={range.min}
                    onChange={(e) => handleConfigChange('recoveryPercentages', key, { ...range, min: Number(e.target.value) })}
                    size="small"
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 1000 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    type="number"
                    value={range.max}
                    onChange={(e) => handleConfigChange('recoveryPercentages', key, { ...range, max: Number(e.target.value) })}
                    size="small"
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 1000 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const GCCSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        GCC-Specific Configuration
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Configure GCC-specific impact percentages for establishment, operational, and value amplification phases.
      </Alert>

      {Object.entries(config.gccConstants).map(([phase, phaseConfig]) => (
        <Card key={phase} variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
              {phase.replace(/([A-Z])/g, ' $1').trim()} Phase
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(phaseConfig).map(([area, range]) => (
                <Grid item xs={12} md={6} key={area}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom sx={{ textTransform: 'capitalize' }}>
                      {area.replace(/([A-Z])/g, ' $1').trim()}
                    </Typography>
                    {range.min !== undefined ? (
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          label="Min %"
                          type="number"
                          value={range.min}
                          onChange={(e) => handleConfigChange('gccConstants', phase, { ...phaseConfig, [area]: { ...range, min: Number(e.target.value) } })}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Max %"
                          type="number"
                          value={range.max}
                          onChange={(e) => handleConfigChange('gccConstants', phase, { ...phaseConfig, [area]: { ...range, max: Number(e.target.value) } })}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    ) : (
                      <TextField
                        label="Value"
                        type="number"
                        value={range.value}
                        onChange={(e) => handleConfigChange('gccConstants', phase, { ...phaseConfig, [area]: { value: Number(e.target.value) } })}
                        size="small"
                        fullWidth
                      />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const PainPointWeightsSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Pain Point Base Weights
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Configure the base weight for each pain point type (1-10 scale). Higher weights indicate greater importance in pain score calculation.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pain Point Type</TableCell>
              <TableCell align="center">Base Weight (1-10)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(config.painPointBaseWeights).map(([key, weight]) => (
              <TableRow key={key}>
                <TableCell>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Slider
                    value={weight}
                    onChange={(e, newValue) => handleConfigChange('painPointBaseWeights', key, newValue)}
                    min={1}
                    max={10}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{ width: 150, mx: 'auto' }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const FallbackSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        Fallback Values
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        These values are used when specific data is not available. Set conservative defaults.
      </Alert>

      <Grid container spacing={2}>
        {Object.entries(config.fallbackValues).map(([key, value]) => (
          <Grid item xs={12} sm={6} md={3} key={key}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" gutterBottom sx={{ textTransform: 'capitalize' }}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={value}
                  onChange={(e) => handleConfigChange('fallbackValues', key, Number(e.target.value))}
                  size="small"
                  inputProps={{ min: 1, max: 10, step: 0.5 }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const sectionComponents = {
    weights: <WeightsSection />,
    businessCase: <BusinessCaseSection />,
    impactRanges: <ImpactRangesSection />,
    recovery: <RecoverySection />,
    gcc: <GCCSection />,
    painWeights: <PainPointWeightsSection />,
    fallback: <FallbackSection />
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Settings color="primary" />
            <Typography variant="h5" component="h2">
              Calculation Configuration
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Configure all calculation parameters for value leakage analysis
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Box sx={{ display: 'flex', overflowX: 'auto' }}>
            {[
              { key: 'weights', label: 'Scoring Weights' },
              { key: 'businessCase', label: 'Business Case' },
              { key: 'impactRanges', label: 'Impact Ranges' },
              { key: 'recovery', label: 'Recovery %' },
              { key: 'gcc', label: 'GCC Specific' },
              { key: 'painWeights', label: 'Pain Weights' },
              { key: 'fallback', label: 'Fallback Values' }
            ].map(({ key, label }) => (
              <Button
                key={key}
                onClick={() => setActiveSection(key)}
                variant={activeSection === key ? "contained" : "text"}
                sx={{ 
                  minWidth: 'auto', 
                  px: 2, 
                  mx: 0.5,
                  borderRadius: 1
                }}
                size="small"
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Active Section Content */}
        {sectionComponents[activeSection]}

        {/* Configuration Summary */}
        <Card sx={{ mt: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Configuration Summary
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <strong>Total Parameters:</strong> {Object.keys(config).reduce((acc, key) => acc + Object.keys(config[key]).length, 0)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <strong>Pain Points:</strong> {Object.keys(config.painPointBaseWeights).length}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <strong>Impact Types:</strong> {Object.keys(config.impactRanges).length}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <strong>Recovery Types:</strong> {Object.keys(config.recoveryPercentages).length}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleReset}
          startIcon={<Restore />}
          variant="outlined"
          color="warning"
        >
          Reset to Defaults
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<Save />}
          variant="contained"
          disabled={!hasChanges}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalculationConfigurationPanel;
export { DEFAULT_CALCULATION_CONFIG };